import { useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, Phone, Mail, MapPin, Clock, MessageCircle, Globe, Facebook, Instagram, Star, Navigation } from "lucide-react";
import { assetUrl } from "@/lib/api";

export const H = ({ children, className = "", ...p }) => <h2 className={`text-3xl sm:text-4xl ${className}`} style={{ fontFamily: "var(--hfont)" }} {...p}>{children}</h2>;
export const Eyebrow = ({ children }) => <p className="text-[11px] uppercase tracking-[0.3em] mb-3" style={{ color: "var(--accent)" }}>{children}</p>;
export const Section = ({ id, children, alt, testId }) => (
  <section id={id} className="px-6 py-20 sm:py-24" style={alt ? { background: "var(--surface)" } : undefined} data-testid={testId}>
    <div className="max-w-6xl mx-auto">{children}</div>
  </section>
);
const wa = (n) => `https://wa.me/${String(n || "").replace(/\D/g, "")}`;
const ext = (u) => (/^https?:\/\//i.test(u) ? u : `https://${u}`);

const LINKS = [["collection", "Collection"], ["verify", "Verify certificate"], ["about", "About"], ["enquiry", "Enquire"], ["contact", "Contact"]];

export function SiteNav({ brand, site, base }) {
  const [open, setOpen] = useState(false);
  const go = (id) => { setOpen(false); document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }); };
  const home = base || "/";
  return (
    <header className="sticky top-0 z-40 backdrop-blur-md border-b" style={{ background: "color-mix(in srgb, var(--bg) 85%, transparent)", borderColor: "var(--line)" }}>
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to={home} className="flex items-center gap-3" data-testid="site-logo-link">
          {brand.logo && <img src={assetUrl(brand.logo)} alt="" className="h-9 w-9 object-contain" />}
          <span className="text-lg tracking-wide" style={{ fontFamily: "var(--hfont)" }} data-testid="site-store-name">{brand.store_name || site.hero_title}</span>
        </Link>
        <nav className="hidden md:flex items-center gap-7 text-sm">
          {LINKS.map(([id, label]) => (
            <Link key={id} to={`${home}#${id}`} onClick={() => setTimeout(() => go(id), 50)} className="opacity-80 hover:opacity-100 transition-opacity" data-testid={`site-nav-${id}`}>{label}</Link>
          ))}
        </nav>
        <button className="md:hidden" onClick={() => setOpen(!open)} data-testid="site-menu-toggle">{open ? <X size={22} /> : <Menu size={22} />}</button>
      </div>
      {open && (
        <div className="md:hidden px-6 pb-4 flex flex-col gap-3 text-sm" data-testid="site-mobile-menu">
          {LINKS.map(([id, label]) => <Link key={id} to={`${home}#${id}`} onClick={() => setTimeout(() => go(id), 50)}>{label}</Link>)}
        </div>
      )}
    </header>
  );
}

export function Hero({ site, brand, theme }) {
  const img = assetUrl(site.hero_image) || theme.hero;
  return (
    <section className="relative min-h-[78vh] flex items-end overflow-hidden" data-testid="site-hero">
      <img src={img} alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0" style={{ background: theme.dark ? "linear-gradient(to top, var(--bg) 4%, rgba(0,0,0,0.25) 55%, rgba(0,0,0,0.15))" : "linear-gradient(to top, rgba(20,12,8,0.78), rgba(20,12,8,0.35) 55%, rgba(0,0,0,0.1))" }} />
      <div className="relative max-w-6xl mx-auto w-full px-6 pb-20 rise">
        <p className="text-[11px] uppercase tracking-[0.3em] mb-3" style={{ color: theme.dark ? "var(--accent)" : "#fff", opacity: theme.dark ? 1 : 0.9 }} data-testid="site-hero-eyebrow">{brand.city ? `Fine jewellery · ${brand.city}` : "Fine jewellery"}</p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl max-w-3xl leading-[1.05]" style={{ fontFamily: "var(--hfont)", color: theme.dark ? "var(--text)" : "#fff", textShadow: theme.dark ? "none" : "0 2px 18px rgba(0,0,0,.35)" }} data-testid="site-hero-title">{site.hero_title}</h1>
        <p className="mt-5 max-w-xl text-base opacity-90" style={{ color: theme.dark ? "var(--muted)" : "#fff" }}>{site.hero_subtitle}</p>
        <div className="mt-8 flex flex-wrap gap-3">
          <a href="#collection" className="px-6 py-3 text-sm font-semibold rounded-full transition-transform hover:-translate-y-0.5" style={{ background: "var(--accent)", color: "#fff" }} data-testid="site-hero-collection-btn">View collection</a>
          <a href="#verify" className="px-6 py-3 text-sm font-semibold rounded-full border backdrop-blur-sm transition-colors" style={{ borderColor: theme.dark ? "var(--accent)" : "#fff", color: theme.dark ? "var(--text)" : "#fff", background: "rgba(0,0,0,0.25)" }} data-testid="site-hero-verify-btn">Verify certificate</a>
        </div>
      </div>
    </section>
  );
}

export function Rates({ site }) {
  const r = site.rates || {};
  const items = [["24K Gold", r.gold24], ["22K Gold", r.gold22], ["18K Gold", r.gold18], ["Silver", r.silver]].filter(([, v]) => v);
  if (!items.length) return null;
  return (
    <section className="px-6 -mt-10 relative z-10" data-testid="site-rates">
      <div className="max-w-6xl mx-auto rounded-2xl border p-6 grid grid-cols-2 md:grid-cols-5 gap-6 items-center shadow-xl" style={{ background: "var(--surface)", borderColor: "var(--line)" }}>
        <div>
          <p className="text-xs uppercase tracking-[0.25em]" style={{ color: "var(--accent)" }}>Today's rate</p>
          <p className="text-[11px] mt-1" style={{ color: "var(--muted)" }}>{r.note}{r.updated_at ? ` · ${new Date(r.updated_at).toLocaleDateString()}` : ""}</p>
        </div>
        {items.map(([k, v]) => (
          <div key={k} data-testid={`site-rate-${k.replace(/\W/g, "").toLowerCase()}`}>
            <p className="text-xs" style={{ color: "var(--muted)" }}>{k}</p>
            <p className="text-xl font-semibold" style={{ fontFamily: "var(--hfont)" }}>{v}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

export function About({ site, brand, theme }) {
  if (!site.about) return null;
  return (
    <Section id="about" alt testId="site-about">
      <div className="grid md:grid-cols-2 gap-12 items-center">
        <img src={assetUrl(site.about_image) || theme.hero} alt="" className="w-full aspect-[4/5] object-cover rounded-2xl" />
        <div>
          <Eyebrow>About {brand.store_name}</Eyebrow>
          <H>{site.about_title}</H>
          <p className="mt-6 leading-relaxed whitespace-pre-line" style={{ color: "var(--muted)" }}>{site.about}</p>
        </div>
      </div>
    </Section>
  );
}

const Row = ({ icon: Icon, children, href, testId }) => (
  <a href={href} target={href?.startsWith("http") ? "_blank" : undefined} rel="noreferrer" className="flex items-start gap-3 py-2 hover:opacity-80" data-testid={testId}>
    <Icon size={18} style={{ color: "var(--accent)" }} className="mt-0.5 shrink-0" /> <span>{children}</span>
  </a>
);

export function Contact({ site, brand }) {
  const q = site.map_query || brand.address;
  return (
    <Section id="contact" alt testId="site-contact">
      <div className="grid md:grid-cols-2 gap-10">
        <div>
          <Eyebrow>Visit the showroom</Eyebrow>
          <H>Contact us</H>
          <div className="mt-6 text-sm space-y-1">
            {brand.address && <Row icon={MapPin} href={brand.maps_link ? ext(brand.maps_link) : `https://maps.google.com/?q=${encodeURIComponent(brand.address)}`} testId="site-contact-address">{brand.address}</Row>}
            {brand.phone && <Row icon={Phone} href={`tel:${brand.phone}`} testId="site-contact-phone">{brand.phone}</Row>}
            {brand.whatsapp && <Row icon={MessageCircle} href={wa(brand.whatsapp)} testId="site-contact-whatsapp">WhatsApp {brand.whatsapp}</Row>}
            {brand.email && <Row icon={Mail} href={`mailto:${brand.email}`} testId="site-contact-email">{brand.email}</Row>}
            {brand.website && <Row icon={Globe} href={/^https?:/.test(brand.website) ? brand.website : `https://${brand.website}`}>{brand.website}</Row>}
            {site.hours && <Row icon={Clock}>{site.hours}</Row>}
          </div>
          <div className="flex flex-wrap gap-3 mt-6">
            {(brand.maps_link || brand.address) && (
              <a href={brand.maps_link ? ext(brand.maps_link) : `https://maps.google.com/?q=${encodeURIComponent(brand.address)}`} target="_blank" rel="noreferrer"
                className="px-5 py-2.5 rounded-full text-sm font-semibold flex items-center gap-2" style={{ background: "var(--accent)", color: "#fff" }} data-testid="site-directions-btn"><Navigation size={15} /> Get directions</a>
            )}
            {brand.google_review && (
              <a href={ext(brand.google_review)} target="_blank" rel="noreferrer" className="px-5 py-2.5 rounded-full text-sm font-semibold border flex items-center gap-2" style={{ borderColor: "var(--accent)" }} data-testid="site-google-review-btn">
                <Star size={15} style={{ color: "var(--accent)", fill: "var(--accent)" }} /> Rate us on Google
              </a>
            )}
          </div>
          <Socials brand={brand} className="mt-6" />
        </div>
        {q && <iframe title="map" className="w-full h-80 rounded-2xl border" style={{ borderColor: "var(--line)" }} loading="lazy" src={`https://maps.google.com/maps?q=${encodeURIComponent(q)}&output=embed`} data-testid="site-map" />}
      </div>
    </Section>
  );
}

export function Socials({ brand, className = "" }) {
  const items = [[brand.facebook, Facebook, "Facebook", "facebook"], [brand.instagram, Instagram, "Instagram", "instagram"], [brand.google_review, Star, "Google reviews", "google"], [brand.maps_link, MapPin, "Google Maps", "maps"]].filter(([u]) => u);
  if (!items.length) return null;
  return (
    <div className={`flex gap-3 ${className}`} data-testid="site-socials">
      {items.map(([u, Icon, label, k]) => (
        <a key={k} href={ext(u)} target="_blank" rel="noreferrer" title={label} className="w-10 h-10 rounded-full border grid place-items-center hover:-translate-y-0.5 transition-transform" style={{ borderColor: "var(--line)", color: "var(--accent)" }} data-testid={`site-social-${k}`}><Icon size={17} /></a>
      ))}
    </div>
  );
}

export function SiteFooter({ brand }) {
  return (
    <footer className="px-6 py-10 text-center text-xs border-t space-y-4" style={{ borderColor: "var(--line)", color: "var(--muted)" }} data-testid="site-footer">
      <Socials brand={brand} className="justify-center" />
      <p>© {new Date().getFullYear()} {brand.store_name} · BIS hallmarked jewellery · Certificates verifiable online</p>
    </footer>
  );
}

export function WhatsAppFab({ brand }) {
  const n = brand.whatsapp || brand.phone;
  if (!n) return null;
  return (
    <a href={`${wa(n)}?text=${encodeURIComponent(`Hello ${brand.store_name || ""}, I'd like to know more about your jewellery.`)}`} target="_blank" rel="noreferrer"
      className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full grid place-items-center shadow-2xl bg-[#25D366] text-white hover:scale-105 transition-transform" data-testid="site-whatsapp-fab" title="Chat on WhatsApp">
      <MessageCircle size={26} />
    </a>
  );
}
